<template>
  <div>
    <v-text-field
      v-mask="maskOptions"
      :value="ageInput"
      :autofocus="autofocus"
      :label="t('Возраст пациента', options)"
      clear-icon="ui-icon-close-not-a-circle"
      hide-details
      clearable
      outlined
      type="tel"
      maxlength="3"
      inputmode="numeric"
      @input="handleInput"
    />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  ref,
  toRefs,
  watch,
} from 'vue'
import I18NextVue from 'i18next'
import { debounce } from 'www/utils'
import { mask } from 'vue-the-mask'
import useFiltersStore from 'www/vue_components/common/TheFilters/doctors/stores/useFiltersStore'
import type { Filters } from 'www/vue_components/common/TheFilters/doctors/stores/useFiltersStore.types'

const vMask = mask
const maskOptions = {
  mask: ['#', '%#', '%##'],
  tokens: {
    '%': { pattern: /[1-9]/ },
    '#': { pattern: /[0-9]/ },
  },
}

const emit = defineEmits(['input'])
const props = defineProps<{
  value: Filters,
  autofocus?: boolean
}>()
const { value: doctorFiltersState } = toRefs(props)

const filtersStore = useFiltersStore()

const { t, language } = I18NextVue
const options = computed(() => ({ ns: language }))

const ageInput = ref<Filters['patientAge']>(doctorFiltersState.value.patientAge)

watch(() => doctorFiltersState.value.patientAge, () => {
  ageInput.value = doctorFiltersState.value.patientAge
})

const getCorrectAge = (age: Filters['patientAge'] | string) => {
  const numberAge = parseInt(`${age}`, 10)

  if (Number.isNaN(numberAge)) {
    return undefined
  }

  return numberAge > 100 ? 100 : numberAge
}

const getCorrectSpeciality = (age: Filters['patientAge']) => {
  const currentSpeciality = doctorFiltersState.value.speciality
  const adultSpeciality = doctorFiltersState.value.speciality.adult
  const childrenSpeciality = doctorFiltersState.value.speciality.children

  // Устанавливаем исходную специальность
  if (age === undefined) {
    return currentSpeciality
  }

  // Устанавливаем детскую специальность если возраст пациента меньше 18
  if (age < 18 && childrenSpeciality) {
    return {
      ...childrenSpeciality,
      adult: currentSpeciality,
      children: undefined,
    }
  }

  // Устанавливаем взрослую специальность если возраст пациента 18 или больше
  if (age >= 18 && adultSpeciality) {
    return {
      ...adultSpeciality,
      adult: undefined,
      children: currentSpeciality,
    }
  }

  return currentSpeciality
}

const emitChangesInput = (age: Filters['patientAge']) => {
  const patientAge = getCorrectAge(age)
  const speciality = getCorrectSpeciality(age)
  const otherSpeciality = speciality.id === doctorFiltersState.value.otherSpeciality?.id
    ? undefined
    : doctorFiltersState.value.otherSpeciality

  filtersStore.updateUnsavedFilters({
    patientAge,
    speciality,
    otherSpeciality,
  })
  emit('input', {
    ...doctorFiltersState.value,
    patientAge,
    speciality,
    otherSpeciality,
  })
}

const debouncedEmitChanges = debounce(emitChangesInput, 500)

const handleInput = (age: Filters['patientAge'] | null) => {
  if (age === null) { // Если возраст сбросили нажатием на крестик
    if (doctorFiltersState.value.patientAge !== undefined) {
      emitChangesInput(undefined)
    }

    return
  }

  ageInput.value = getCorrectAge(age)

  if (doctorFiltersState.value.patientAge !== ageInput.value) {
    debouncedEmitChanges(ageInput.value)
  }
}
</script>
